import React from "react";

/* Motion primitives. MASTER_SPEC Draft80 LUX-11, LUX-12, LUX-16.
 *
 * The contract's hardest motion rule is a negative one: reduced motion must
 * leave a *complete* experience, not a broken one. So nothing here gates
 * content on an animation finishing, and every hook has a defined answer when
 * the browser API it wants is missing.
 */

/** True when the visitor has asked for less motion, kept live.
 *
 *  Read at render rather than once at import: a person can change the setting
 *  while the page is open, and on a long marketing page they may well do it
 *  *because* of the page.
 */
export function useReducedMotion() {
  const [reduced, setReduced] = React.useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/** True on a coarse pointer. Hover and cursor-tracking are desktop-only
 *  affordances (LUX-15); on touch they are dead weight and battery. */
export function useIsTouch() {
  const [touch, setTouch] = React.useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(hover: none), (pointer: coarse)").matches;
  });
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(hover: none), (pointer: coarse)");
    const onChange = (e) => setTouch(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return touch;
}

/** Reveal an element once when it enters the viewport.
 *
 *  Returns a ref and the `data-revealed` value to spread. Without
 *  IntersectionObserver the value is "unsupported", which the stylesheet shows
 *  outright — a browser that cannot observe must not be left with an invisible
 *  page.
 *
 *  The observer disconnects after firing: a marketing page has forty of these
 *  and none of them needs to keep watching.
 */
export function useInViewReveal({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = React.useRef(null);
  const reduced = useReducedMotion();
  const [revealed, setRevealed] = React.useState(false);
  const [supported, setSupported] = React.useState(true);

  React.useEffect(() => {
    // Under reduced motion there is nothing to reveal — the content is simply
    // present. Gating it on an observer would make a preference for less
    // movement into a dependency on scrolling.
    if (reduced) { setRevealed(true); return undefined; }
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSupported(false);
      return undefined;
    }
    // The failure mode this guards against: an element that starts at
    // opacity 0 and never gets observed stays invisible forever. It happens
    // with a resized viewport, a print stylesheet, a headless capture, or a
    // browser quirk — and the result is a blank page, not a missing
    // animation. So the reveal fires on a timer regardless, and the observer
    // only ever makes it happen sooner.
    const safety = window.setTimeout(() => setRevealed(true), 1200);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            window.clearTimeout(safety);
            setRevealed(true);
            observer.disconnect();
          }
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(node);
    return () => { window.clearTimeout(safety); observer.disconnect(); };
  }, [threshold, rootMargin, reduced]);

  return [ref, supported ? String(revealed) : "unsupported"];
}

/** A section that fades and lifts into place once. */
export function Reveal({ as: Tag = "div", delay = 0, className = "", style, children, ...rest }) {
  const [ref, revealed] = useInViewReveal();
  return (
    <Tag
      ref={ref}
      data-revealed={revealed}
      className={`cc-reveal ${className}`.trim()}
      style={{ transitionDelay: delay ? `${delay}ms` : undefined, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** Pointer tilt for the hero postcard.
 *
 *  Desktop only, and capped at ±4° — enough to read as a physical object
 *  catching the light, not enough to feel like a toy. Disabled entirely under
 *  reduced motion and on touch, where there is no pointer to track and the
 *  listener would cost frames for nothing.
 */
export function usePointerTilt({ max = 4 } = {}) {
  const ref = React.useRef(null);
  const reduced = useReducedMotion();
  const touch = useIsTouch();
  const active = !reduced && !touch;

  React.useEffect(() => {
    const node = ref.current;
    if (!node || !active) return undefined;
    let frame = 0;

    const onMove = (event) => {
      if (frame) return;                  // one update per frame, never a loop
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const box = node.getBoundingClientRect();
        const x = (event.clientX - box.left) / box.width - 0.5;
        const y = (event.clientY - box.top) / box.height - 0.5;
        node.style.setProperty("--tilt-y", `${(x * max * 2).toFixed(2)}deg`);
        node.style.setProperty("--tilt-x", `${(-y * max * 2).toFixed(2)}deg`);
      });
    };
    const onLeave = () => {
      node.style.setProperty("--tilt-y", "0deg");
      node.style.setProperty("--tilt-x", "0deg");
    };

    node.addEventListener("pointermove", onMove);
    node.addEventListener("pointerleave", onLeave);
    return () => {
      node.removeEventListener("pointermove", onMove);
      node.removeEventListener("pointerleave", onLeave);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [active, max]);

  return ref;
}
